import {useEffect, useState} from 'react';
import {HelmetProvider} from 'react-helmet-async';
import {BrowserRouter, Route, Routes, useLocation, useNavigate} from 'react-router-dom';
import Header from './components/Header';
import LessonView from './components/LessonView';
import ModuleView from './components/ModuleView';
import NotFound from './components/NotFound';
import RouteMetadata from './components/RouteMetadata';
import ScrollToTop from './components/ScrollToTop';
import TransparencyBanner from './components/TransparencyBanner';
import ProgressDashboard from './components/ProgressDashboard';
import CompletionModal from './components/CompletionModal';
import EmCalculator from './components/EmCalculator';
import Icd10Quiz from './components/Icd10Quiz';
import ModifierTool from './components/ModifierTool';
import TermDictionary from './components/TermDictionary';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import DisclaimerPage from './pages/DisclaimerPage';
import {domains} from './data/lessons';
import useProgress from './hooks/useProgress';
import usePwaInstall from './hooks/usePwaInstall';
import './App.css';
import './LessonView.css';
import './components/InformationPage.css';

function RouteLifecycle() {
  const location = useLocation();

  useEffect(() => {
    window.prerenderReady = false;
    window.scrollTo({top: 0, left: 0, behavior: 'auto'});
  }, [location.pathname, location.search]);

  return null;
}

function AppContent() {
  const navigate = useNavigate();
  const {completedLessons, completionPercentage, toggleLesson} = useProgress();
  const [showProgressDashboard, setShowProgressDashboard] = useState(false);
  const [completedLessonId, setCompletedLessonId] = useState(null);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const {
    deferredPrompt,
    installApp,
    iosSafari,
    isInstalled,
    canShowPwaCallout,
  } = usePwaInstall();

  const lessons = domains.flatMap((domain) => domain.lessons);
  const nextLesson = lessons.find(
    (lesson) => !completedLessons.includes(lesson.id),
  );

  const handleToggleLesson = (lessonId) => {
    const wasCompleted = completedLessons.includes(lessonId);
    toggleLesson(lessonId);

    if (!wasCompleted) {
      setCompletedLessonId(lessonId);
      setShowCompletionModal(true);

      if (
        !isInstalled &&
        canShowPwaCallout() &&
        localStorage.getItem('pwa_prompt_shown') !== 'true'
      ) {
        localStorage.setItem('pwa_prompt_shown', 'true');
      }
    }
  };

  const continueStudying = () => {
    setShowCompletionModal(false);
    const currentIndex = lessons.findIndex(
      (lesson) => lesson.id === completedLessonId,
    );
    const followingLesson = lessons[currentIndex + 1];

    if (followingLesson) {
      const nextDomain = domains.find((domain) =>
        domain.lessons.some((lesson) => lesson.id === followingLesson.id),
      );

      if (nextDomain) {
        navigate(`/lesson/${nextDomain.id}/${followingLesson.id}`);
      }
    }
  };

  useEffect(() => {
    if (showProgressDashboard) {
      window.scrollTo({top: 0, left: 0, behavior: 'auto'});
    }
  }, [showProgressDashboard]);

  const closeProgressDashboard = () => {
    setShowProgressDashboard(false);
    window.scrollTo({top: 0, left: 0, behavior: 'auto'});
  };

  return (
    <>
      <RouteLifecycle />
      <ScrollToTop />
      <div className="app-shell">
        <Header
          completionPercentage={completionPercentage}
          onViewProgress={() => setShowProgressDashboard((current) => !current)}
          onCloseProgress={closeProgressDashboard}
        />

        {showProgressDashboard ? (
          <ProgressDashboard
            domains={domains}
            completedLessons={completedLessons}
            onToggleLesson={handleToggleLesson}
            onClose={closeProgressDashboard}
          />
        ) : (
          <Routes>
            <Route
              path="/"
              element={<HomePage domains={domains} completedLessons={completedLessons} />}
            />
            <Route
              path="/module/:moduleId"
              element={
                <ModuleView
                  domains={domains}
                  completedLessons={completedLessons}
                  onToggleLesson={handleToggleLesson}
                />
              }
            />
            <Route
              path="/modules/:slug"
              element={
                <ModuleView
                  domains={domains}
                  completedLessons={completedLessons}
                  onToggleLesson={handleToggleLesson}
                />
              }
            />
            <Route
              path="/lesson/:domainId/:lessonId"
              element={
                <LessonView
                  domains={domains}
                  completedLessons={completedLessons}
                  completionPercentage={completionPercentage}
                  onToggleLesson={handleToggleLesson}
                />
              }
            />
            <Route path="/em-calculator" element={<EmCalculator />} />
            <Route path="/term-dictionary" element={<TermDictionary />} />
            <Route path="/modifier-tool" element={<ModifierTool />} />
            <Route path="/icd10-quiz" element={<Icd10Quiz />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/disclaimer" element={<DisclaimerPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        )}

        <RouteMetadata />
        <TransparencyBanner />

        {showCompletionModal && (
          <CompletionModal
            completionPercentage={completionPercentage}
            nextLesson={nextLesson}
            showPwaCallout={
              completedLessons.length === 1 &&
              localStorage.getItem('pwa_prompt_shown') === 'true' &&
              !isInstalled
            }
            deferredPrompt={deferredPrompt}
            iosSafari={iosSafari}
            onInstall={installApp}
            onContinue={continueStudying}
          />
        )}
      </div>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;